import { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionManager } from 'react-native';
import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

// useVendorOrders.ts
export function useVendorOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Prevent duplicate concurrent fetches
  const isFetchingRef = useRef(false);

  // Listen for auth changes to update userId (same pattern as useOrders)
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id ?? null);
    };
    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!userId) {
      // No user logged in - only clear orders on initial load, not on refetch
      // This prevents flash when userId briefly becomes null during focus
      if (!hasLoadedOnce) {
        setOrders([]);
        setError(null);
        setLoading(false);
      }
      return;
    }
    
    // Prevent duplicate concurrent fetches - this is key for preventing the cascade
    if (isFetchingRef.current) {
      console.log("🎯 [useVendorOrders] Skipping duplicate fetch - already in progress");
      return;
    }
    isFetchingRef.current = true;

    try {
      // Only show loading state on initial load, not on refetch
      // This prevents layout shift when navigating back to the screen
      if (!hasLoadedOnce) {
        setLoading(true);
      }
      setError(null);

      console.log("🎯 [useVendorOrders] Fetching vendor orders for user:", userId);

      // First, get the vendor ID for this user
      const { data: vendorRow, error: vendorError } = await supabase
        .from("vendors")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (vendorError) {
        console.error("🎯 [useVendorOrders] Error fetching vendor:", vendorError.message);
        throw vendorError;
      }

      if (!vendorRow?.id) {
        console.log("🎯 [useVendorOrders] User is not a vendor, skipping fetch");
        setOrders([]);
        setHasLoadedOnce(true);
        setLoading(false);
        return;
      }

      console.log("🎯 [useVendorOrders] Fetching orders for vendor:", vendorRow.id);

      // Explicitly filter by vendor_id to prevent seeing buyer orders
      // This is critical - don't rely solely on RLS policies
      const { data, error: fetchError } = await supabase
        .from("vendor_orders")
        .select("*")
        .eq("vendor_id", vendorRow.id)
        .order("created_at", { ascending: false });

      // Note: Removed verbose logging of full order data - was blocking JS thread
      console.log("🎯 [useVendorOrders] Query complete:", { count: data?.length ?? 0, error: fetchError?.message ?? null });

      if (fetchError) throw fetchError;

      const mapped = (data ?? []).map((o: any) => ({
        id: o.id,
        created_at: o.created_at,
        updated_at: o.updated_at ?? null,
        status: o.status,
        total_amount: Number(o.total_amount) || 0,
        items: (o.items ?? []).map((i: any) => ({ 
          name: i.name, 
          quantity: i.quantity,
          product_id: i.product_id ?? null,
        })),
        tracking_url: o.tracking_url ?? null,
        delivery_status: o.delivery_status ?? null,
        delivery_id: o.delivery_id ?? null,
        parent_order_id: o.parent_order_id ?? null,
        buyer_id: o.buyer_id ?? null,
      }));

      console.log("🎯 [useVendorOrders] Mapped orders:", mapped.length, mapped.map(o => ({ id: o.id.slice(0, 8), status: o.status })));

      setOrders(mapped);
      setHasLoadedOnce(true);
    } catch (e: any) {
      console.error("🎯 [useVendorOrders] Error:", e.message);
      setError(e.message || "Failed to load orders");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [userId, hasLoadedOnce]);

  // Real-time subscription setup
  const setupRealtimeSubscription = useCallback(() => {
    let channel: RealtimeChannel | null = null;

    const initSubscription = async () => {
      if (!userId) return;

      console.log("🎯 [useVendorOrders] Setting up realtime subscription for user:", userId);

      // Get vendor ID for filtering
      const { data: vendorRow } = await supabase
        .from('vendors')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!vendorRow?.id) {
        console.log("🎯 [useVendorOrders] No vendor found, skipping subscription");
        return;
      }

      console.log("🎯 [useVendorOrders] Setting up subscription for vendor:", vendorRow.id);

      channel = supabase
        .channel(`vendor-orders-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'vendor_orders',
            filter: `vendor_id=eq.${vendorRow.id}`,
          },
          (payload) => {
            console.log('🎯 [useVendorOrders] Vendor order updated:', payload.new?.id);
            const updatedOrder = payload.new;
            
            setOrders(prevOrders => 
              prevOrders.map(order => 
                order.id === updatedOrder.id 
                  ? { 
                      ...order, 
                      status: updatedOrder.status,
                      tracking_url: updatedOrder.tracking_url || order.tracking_url,
                      total_amount: Number(updatedOrder.total_amount) || order.total_amount,
                      items: updatedOrder.items || order.items,
                      delivery_status: updatedOrder.delivery_status || order.delivery_status,
                      delivery_id: updatedOrder.delivery_id || order.delivery_id,
                      parent_order_id: updatedOrder.parent_order_id || order.parent_order_id,
                      buyer_id: updatedOrder.buyer_id || order.buyer_id,
                    }
                  : order
              )
            );
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'vendor_orders',
            filter: `vendor_id=eq.${vendorRow.id}`,
          },
          (payload) => {
            console.log('🎯 [useVendorOrders] New vendor order:', payload.new?.id);
            const newOrderData = payload.new;
            
            // Map the new order to our expected format
            const newOrder = {
              id: newOrderData.id,
              created_at: newOrderData.created_at,
              status: newOrderData.status,
              total_amount: Number(newOrderData.total_amount) || 0,
              items: (newOrderData.items ?? []).map((i: any) => ({ 
                name: i.name, 
                quantity: i.quantity,
                product_id: i.product_id ?? null,
              })),
              tracking_url: newOrderData.tracking_url ?? null,
              delivery_status: newOrderData.delivery_status ?? null,
              delivery_id: newOrderData.delivery_id ?? null,
              parent_order_id: newOrderData.parent_order_id ?? null,
              buyer_id: newOrderData.buyer_id ?? null,
            };
            
            setOrders(prevOrders => [newOrder, ...prevOrders]);
          }
        )
        .subscribe((status) => {
          console.log('🎯 [useVendorOrders] Subscription status:', status);
        });
    };

    initSubscription();

    return () => {
      if (channel) {
        console.log("🎯 [useVendorOrders] Cleaning up realtime subscription");
        supabase.removeChannel(channel);
      }
    };
  }, [userId]);

  // Use InteractionManager to defer fetch until animations complete
  // This prevents blocking touches during navigation
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      fetchOrders();
    });
    return () => task.cancel();
  }, [fetchOrders]);

  // Use InteractionManager for subscription setup too
  useEffect(() => {
    if (userId) {
      const task = InteractionManager.runAfterInteractions(() => {
        const cleanup = setupRealtimeSubscription();
        // Store cleanup for later
        task.cancel = () => {
          if (cleanup) cleanup();
        };
      });
      return () => task.cancel();
    }
  }, [setupRealtimeSubscription, userId]);

  return { orders, loading, error, refetch: fetchOrders };
}
